import { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getTranslationValue } from '../../../constants/posMenu';
import {
  buildPathKey,
  buildOptionPathKey,
  buildOptionEntry,
  resolveParentOptionInfo,
  removeNestedGroupsForPrefix,
  isOptionPreselected,
  calculatePriceByType,
  getEffectivePriceType,
  PRICE_TYPES,
  isItemSoldOut,
  isMultiplierConditionActive,
  getEffectiveOptionsCount,
  getEffectiveOptionCount,
  computeTotalItemCount,
  computeActualQtyUpdate,
  applyAncestorDispatch,
  findAllMgInstances,
  shouldDisableForAncestorMax,
  hasModifierItemMinimums,
  meetsModifierItemMinimums,
} from './previewHelpers';
import { POS_COLORS } from './posColors';

/**
 * NestedLayoutModal – mirrors POS client NestedLayoutModal.
 *
 * Opens when a modifier group or option has showNestedLayout === 'popup'.
 * Shows ALL nested modifier groups inline (no internal navigation).
 * When an option with nested MGs is selected, its nested MGs appear below.
 *
 * Props:
 *   open           – boolean, whether the dialog is visible
 *   onClose        – callback to close the dialog (handles save + cleanup)
 *   optionItem     – the option whose nested MGs we're showing
 *   parentMgPathKey – pathKey of the parent modifier group (e.g. "product_X__mg_1")
 *   optionPathKey  – pathKey of the option (e.g. "product_X__mg_1__op_A")
 *   menuMap        – the full menu map
 *   selectedProduct – the currently selected product
 *   modifierGroups – the full modifier groups state
 *   onUpdateModifierGroups – callback to update the modifier groups state
 */
const NestedLayoutModal = ({
  open,
  onClose,
  optionItem,
  parentMgPathKey,
  optionPathKey,
  menuMap,
  selectedProduct,
  modifierGroups,
  onUpdateModifierGroups,
}) => {
  // Top-level MG IDs to show (from the option that opened the popup)
  const topMgIds = optionItem?.modifierGroupIds || [];
  const topParentPK = optionPathKey;

  // Save & close – if no nested selections exist, remove parent option
  const handleSave = useCallback(() => {
    if (!optionItem || !parentMgPathKey || !optionPathKey) {
      onClose();
      return;
    }

    const hasNested = Object.keys(modifierGroups).some((pk) =>
      pk.startsWith(optionPathKey + '__')
    );

    if (!hasNested) {
      // Remove the option from its parent MG
      const updated = { ...modifierGroups };
      const parentMg = updated[parentMgPathKey];
      if (parentMg?.modifierOptions?.[optionItem._id]) {
        const newOpts = { ...parentMg.modifierOptions };
        delete newOpts[optionItem._id];
        if (Object.keys(newOpts).length === 0) {
          delete updated[parentMgPathKey];
        } else {
          updated[parentMgPathKey] = {
            ...parentMg,
            modifierOptions: newOpts,
          };
        }
        onUpdateModifierGroups(updated);
      }
    }

    onClose();
  }, [
    optionItem,
    parentMgPathKey,
    optionPathKey,
    modifierGroups,
    onUpdateModifierGroups,
    onClose,
  ]);

  // -----------------------------------------------------------------------
  // Price-type helpers
  // -----------------------------------------------------------------------
  const applyZeroParentPrice = (groups, optPathKey, parentOptId, childMgOptions) => {
    if (!optPathKey) return;
    const segs = optPathKey.split('__');
    let parentMgPK = null;
    for (let i = segs.length - 1; i >= 0; i--) {
      if (segs[i].startsWith('op_')) {
        parentMgPK = segs.slice(0, i).join('__');
        break;
      }
    }
    if (!parentMgPK || !groups[parentMgPK]) return;
    const mg = groups[parentMgPK];
    if (mg.modifierOptions?.[parentOptId]) {
      const parentOptData = mg.modifierOptions[parentOptId];
      const multiplierChildren = Object.values(childMgOptions || {})
        .filter((o) => o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE)
        .map((o) => ({ count: o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
      const parentBaseAQ =
        parentOptData.calculatedActualQuantity ??
        parentOptData.actualQuantityWithMultiplier ??
        parentOptData.originalPrice ?? 0;
      const aqResult = computeActualQtyUpdate({
        parentMgPathKey: parentMgPK,
        parentOptionId: parentOptId,
        parentBaseActualQty: parentBaseAQ,
        multiplierChildren,
        selectedModifierGroups: groups,
        menuItems: menuMap?.items ?? {},
        menuModifierGroups: menuMap?.modifierGroups ?? {},
      });
      groups[parentMgPK] = {
        ...mg,
        modifierOptions: {
          ...mg.modifierOptions,
          [parentOptId]: {
            ...parentOptData,
            price: 0,
            actualQuantityWithMultiplier: 0,
          },
        },
      };
      applyAncestorDispatch(groups, aqResult);
    }
  };

  const restoreParentPrice = (groups, optPathKey, parentOpt) => {
    if (!optPathKey || !parentOpt) return;
    const segs = optPathKey.split('__');
    let parentMgPK = null;
    for (let i = segs.length - 1; i >= 0; i--) {
      if (segs[i].startsWith('op_')) {
        parentMgPK = segs.slice(0, i).join('__');
        break;
      }
    }
    if (!parentMgPK || !groups[parentMgPK]) return;
    const mg = groups[parentMgPK];
    if (mg.modifierOptions?.[parentOpt._id]) {
      // If parent is also a multiplier option, restore to calculatedPrice
      // (originalPrice is the raw menu price, not the multiplied value)
      const restoredPrice =
        parentOpt.priceType === 'multiplierApplyingToParentPrice'
          ? (parentOpt.calculatedPrice ?? parentOpt.originalPrice ?? parentOpt.price ?? 0)
          : (parentOpt.originalPrice ?? parentOpt.price ?? 0);
      groups[parentMgPK] = {
        ...mg,
        modifierOptions: {
          ...mg.modifierOptions,
          [parentOpt._id]: {
            ...mg.modifierOptions[parentOpt._id],
            price: restoredPrice,
            actualQuantityWithMultiplier:
              mg.modifierOptions[parentOpt._id].calculatedActualQuantity ??
              mg.modifierOptions[parentOpt._id].actualQuantityWithMultiplier ??
              mg.modifierOptions[parentOpt._id].originalPrice ?? 0,
          },
        },
      };
    }
  };

  // -----------------------------------------------------------------------
  // Option click handler – resolves parent info per pathKey (no navigation)
  // -----------------------------------------------------------------------
  const handleOptionClick = useCallback(
    (mgDef, pathKey, optItem) => {
      if (isItemSoldOut(optItem)) return;

      // Resolve parent info from this MG's pathKey
      const { parentOption: localParent, isNested: localIsNested } =
        resolveParentOptionInfo(pathKey, modifierGroups);

      // Derive the parent option's pathKey for applyZeroParentPrice/restoreParentPrice
      const segs = pathKey.split('__');
      let localParentOptPK = null;
      for (let i = segs.length - 1; i >= 0; i--) {
        if (segs[i].startsWith('op_')) {
          localParentOptPK = segs.slice(0, i + 1).join('__');
          break;
        }
      }

      let updatedGroups = { ...modifierGroups };
      const mgSelection = updatedGroups[pathKey];
      const currentOptions = mgSelection
        ? { ...mgSelection.modifierOptions }
        : {};

      const optionId = optItem._id;
      const isAlreadySelected = !!currentOptions[optionId];
      const hasNested =
        optItem.modifierGroupIds && optItem.modifierGroupIds.length > 0;
      const optPK = buildOptionPathKey(pathKey, optionId);

      const clickMgMultiplierCond = {
        applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
        priceType: mgDef.priceType,
      };
      const clickMgMultiplierActive = isMultiplierConditionActive(clickMgMultiplierCond);

      // Build entry with proper price
      const { entry: optEntry, priceResult } = buildOptionEntry({
        optionItem: optItem,
        menuMap,
        modifierGroup: mgDef,
        parentOption: localIsNested ? localParent : null,
        isNestedUnderParentOption: localIsNested,
        optionPathKey: optPK,
      });

      // --- A) Has nested modifiers – just select/toggle, nested MGs render inline ---
      if (hasNested) {
        if (!isAlreadySelected) {
          const aShouldReplaceAll = (() => {
            if (clickMgMultiplierActive && (Number(mgDef.max) || 0)) {
              const curEff = getEffectiveOptionsCount(
                Object.values(currentOptions),
                mgDef.applyValidationToItemsActualQuantity,
                clickMgMultiplierCond
              );
              const newWt = priceResult.originalPrice ?? 0;
              return curEff + newWt > (Number(mgDef.max) || 0);
            }
            return (Number(mgDef.max) || 0) === 1 && !clickMgMultiplierActive;
          })();

          if (aShouldReplaceAll) {
            Object.keys(currentOptions).forEach((oldOptId) => {
              if (oldOptId !== optionId) {
                const oldOptionBranchPrefix = buildOptionPathKey(pathKey, oldOptId);
                updatedGroups = removeNestedGroupsForPrefix(
                  updatedGroups,
                  oldOptionBranchPrefix
                );
              }
            });
            updatedGroups[pathKey] = {
              ...mgDef,
              pathKey,
              hideNameOnOrder:
                menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
                mgDef.hideNameOnOrder,
              inOrderViewLayout:
                menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
                mgDef.inOrderViewLayout,
              sortParentOptionsByThisOptionsSorting:
                menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
                mgDef.sortParentOptionsByThisOptionsSorting,
              groupAllParentsByThisOption:
                menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
                mgDef.groupAllParentsByThisOption,
              modifierOptions: { [optionId]: optEntry },
            };
          } else {
            updatedGroups[pathKey] = {
              ...mgDef,
              pathKey,
              hideNameOnOrder:
                menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
                mgDef.hideNameOnOrder,
              inOrderViewLayout:
                menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
                mgDef.inOrderViewLayout,
              sortParentOptionsByThisOptionsSorting:
                menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
                mgDef.sortParentOptionsByThisOptionsSorting,
              groupAllParentsByThisOption:
                menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
                mgDef.groupAllParentsByThisOption,
              modifierOptions: { ...currentOptions, [optionId]: optEntry },
            };
          }

          if (
            priceResult.shouldZeroParentPrice &&
            localIsNested &&
            localParent
          ) {
            applyZeroParentPrice(
              updatedGroups,
              localParentOptPK,
              localParent._id,
              updatedGroups[pathKey]?.modifierOptions
            );
          }

          onUpdateModifierGroups(updatedGroups, {
            selectedOptionId: optionId,
            parentMgPathKey: pathKey,
            selectedOptionData: optEntry,
          });
        } else {
          // Already selected nested – toggle off (deselect) unless preselected
          if (isOptionPreselected(selectedProduct, pathKey, optionId)) return;

          const unselectedOpt = currentOptions[optionId];

          if (
            unselectedOpt?.priceType === 'multiplierApplyingToParentPrice' &&
            localIsNested &&
            localParent
          ) {
            const otherHasMultiplier = Object.values(currentOptions).some(
              (o) =>
                o._id !== optionId &&
                o.priceType === 'multiplierApplyingToParentPrice'
            );
            if (!otherHasMultiplier) {
              restoreParentPrice(updatedGroups, localParentOptPK, localParent);
            } else {
              const rpSegs = localParentOptPK.split('__');
              let rpParentMgPK = null;
              for (let ri = rpSegs.length - 1; ri >= 0; ri--) {
                if (rpSegs[ri].startsWith('op_')) {
                  rpParentMgPK = rpSegs.slice(0, ri).join('__');
                  break;
                }
              }
              if (rpParentMgPK && updatedGroups[rpParentMgPK]?.modifierOptions?.[localParent._id]) {
                const parentOptData = updatedGroups[rpParentMgPK].modifierOptions[localParent._id];
                const remainingMultiplierChildren = Object.values(currentOptions)
                  .filter((o) => o._id !== optionId && o.priceType === 'multiplierApplyingToParentPrice')
                  .map((o) => ({ count: o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
                const parentBaseAQ = parentOptData.calculatedActualQuantity ?? parentOptData.actualQuantityWithMultiplier ?? parentOptData.originalPrice ?? 0;
                const aqResult = computeActualQtyUpdate({
                  parentMgPathKey: rpParentMgPK,
                  parentOptionId: localParent._id,
                  parentBaseActualQty: parentBaseAQ,
                  multiplierChildren: remainingMultiplierChildren,
                  selectedModifierGroups: updatedGroups,
                  menuItems: menuMap?.items ?? {},
                  menuModifierGroups: menuMap?.modifierGroups ?? {},
                });
                updatedGroups[rpParentMgPK] = {
                  ...updatedGroups[rpParentMgPK],
                  modifierOptions: {
                    ...updatedGroups[rpParentMgPK].modifierOptions,
                    [localParent._id]: {
                      ...parentOptData,
                      price: 0,
                      actualQuantityWithMultiplier: 0,
                    },
                  },
                };
                applyAncestorDispatch(updatedGroups, aqResult);
              }
            }
          }

          delete currentOptions[optionId];
          updatedGroups = removeNestedGroupsForPrefix(updatedGroups, optPK);

          if (Object.keys(currentOptions).length === 0) {
            delete updatedGroups[pathKey];
          } else {
            updatedGroups[pathKey] = {
              ...updatedGroups[pathKey],
              modifierOptions: currentOptions,
            };
          }
          onUpdateModifierGroups(updatedGroups);
        }
        return;
      }

      // --- B) Already selected leaf: toggle off (unless preselected)
      if (isAlreadySelected) {
        if (isOptionPreselected(selectedProduct, pathKey, optionId)) return;

        const unselectedOpt = currentOptions[optionId];

        if (
          unselectedOpt?.priceType === 'multiplierApplyingToParentPrice' &&
          localIsNested &&
          localParent
        ) {
          const otherHasMultiplier = Object.values(currentOptions).some(
            (o) =>
              o._id !== optionId &&
              o.priceType === 'multiplierApplyingToParentPrice'
          );
          if (!otherHasMultiplier) {
            restoreParentPrice(updatedGroups, localParentOptPK, localParent);
          } else {
            const segs = localParentOptPK.split('__');
            let parentMgPK = null;
            for (let i = segs.length - 1; i >= 0; i--) {
              if (segs[i].startsWith('op_')) {
                parentMgPK = segs.slice(0, i).join('__');
                break;
              }
            }
            if (parentMgPK && updatedGroups[parentMgPK]?.modifierOptions?.[localParent._id]) {
              const parentOptData = updatedGroups[parentMgPK].modifierOptions[localParent._id];
              const remainingMultiplierChildren = Object.values(currentOptions)
                .filter((o) => o._id !== optionId && o.priceType === 'multiplierApplyingToParentPrice')
                .map((o) => ({ count: o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
              const parentBaseAQ = parentOptData.calculatedActualQuantity ?? parentOptData.actualQuantityWithMultiplier ?? parentOptData.originalPrice ?? 0;
              const aqResult = computeActualQtyUpdate({
                parentMgPathKey: parentMgPK,
                parentOptionId: localParent._id,
                parentBaseActualQty: parentBaseAQ,
                multiplierChildren: remainingMultiplierChildren,
                selectedModifierGroups: updatedGroups,
                menuItems: menuMap?.items ?? {},
                menuModifierGroups: menuMap?.modifierGroups ?? {},
              });
              updatedGroups[parentMgPK] = {
                ...updatedGroups[parentMgPK],
                modifierOptions: {
                  ...updatedGroups[parentMgPK].modifierOptions,
                  [localParent._id]: {
                    ...parentOptData,
                    price: 0,
                    actualQuantityWithMultiplier: 0,
                  },
                },
              };
              applyAncestorDispatch(updatedGroups, aqResult);
            }
          }
        }

        delete currentOptions[optionId];
        updatedGroups = removeNestedGroupsForPrefix(updatedGroups, optPK);

        if (Object.keys(currentOptions).length === 0) {
          delete updatedGroups[pathKey];
        } else {
          updatedGroups[pathKey] = {
            ...updatedGroups[pathKey],
            modifierOptions: currentOptions,
          };
        }
        onUpdateModifierGroups(updatedGroups);
        return;
      }

      // --- C) Leaf: compute shouldReplaceAll (covers max===1 single-select AND multiplier weight exceeds)
      const shouldReplaceAll = (() => {
        if (clickMgMultiplierActive && (Number(mgDef.max) || 0)) {
          const curEff = getEffectiveOptionsCount(
            Object.values(currentOptions),
            mgDef.applyValidationToItemsActualQuantity,
            clickMgMultiplierCond
          );
          const newWt = priceResult.originalPrice ?? 0;
          return curEff + newWt > (Number(mgDef.max) || 0);
        }
        return (Number(mgDef.max) || 0) === 1;
      })();

      if (shouldReplaceAll) {
        Object.keys(currentOptions).forEach((oldOptId) => {
          if (oldOptId !== optionId) {
            const oldOpt = currentOptions[oldOptId];
            if (
              oldOpt?.priceType === 'multiplierApplyingToParentPrice' &&
              localIsNested &&
              localParent
            ) {
              restoreParentPrice(updatedGroups, localParentOptPK, localParent);
            }
            const oldOptionBranchPrefix = buildOptionPathKey(pathKey, oldOptId);
            updatedGroups = removeNestedGroupsForPrefix(
              updatedGroups,
              oldOptionBranchPrefix
            );
          }
        });
        updatedGroups[pathKey] = {
          ...mgDef,
          pathKey,
          hideNameOnOrder:
            menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
            mgDef.hideNameOnOrder,
          inOrderViewLayout:
            menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
            mgDef.inOrderViewLayout,
          sortParentOptionsByThisOptionsSorting:
            menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
            mgDef.sortParentOptionsByThisOptionsSorting,
          groupAllParentsByThisOption:
            menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
            mgDef.groupAllParentsByThisOption,
          modifierOptions: { [optionId]: optEntry },
        };

        if (priceResult.shouldZeroParentPrice && localIsNested && localParent) {
          applyZeroParentPrice(
            updatedGroups,
            localParentOptPK,
            localParent._id,
            updatedGroups[pathKey]?.modifierOptions
          );
        }

        onUpdateModifierGroups(updatedGroups, {
          selectedOptionId: optionId,
          parentMgPathKey: pathKey,
          selectedOptionData: optEntry,
        });
        return;
      }

      // --- D) Leaf: multi-select → add ---
      if ((Number(mgDef.max) || 0) > 0) {
        const currentCount = getEffectiveOptionsCount(
          Object.values(currentOptions),
          mgDef.applyValidationToItemsActualQuantity,
          clickMgMultiplierCond
        );
        if (currentCount >= (Number(mgDef.max) || 0)) return;
      }

      currentOptions[optionId] = optEntry;
      updatedGroups[pathKey] = {
        ...mgDef,
        pathKey,
        hideNameOnOrder:
          menuMap?.modifierGroups?.[mgDef._id]?.hideNameOnOrder ??
          mgDef.hideNameOnOrder,
        inOrderViewLayout:
          menuMap?.modifierGroups?.[mgDef._id]?.inOrderViewLayout ??
          mgDef.inOrderViewLayout,
        sortParentOptionsByThisOptionsSorting:
          menuMap?.modifierGroups?.[mgDef._id]?.sortParentOptionsByThisOptionsSorting ??
          mgDef.sortParentOptionsByThisOptionsSorting,
        groupAllParentsByThisOption:
          menuMap?.modifierGroups?.[mgDef._id]?.groupAllParentsByThisOption ??
          mgDef.groupAllParentsByThisOption,
        modifierOptions: currentOptions,
      };

      if (priceResult.shouldZeroParentPrice && localIsNested && localParent) {
        applyZeroParentPrice(updatedGroups, localParentOptPK, localParent._id, updatedGroups[pathKey]?.modifierOptions);
      }

      onUpdateModifierGroups(updatedGroups, {
        selectedOptionId: optionId,
        parentMgPathKey: pathKey,
        selectedOptionData: optEntry,
      });
    },
    [modifierGroups, menuMap, selectedProduct, onUpdateModifierGroups]
  );

  // -----------------------------------------------------------------------
  // Increment / decrement
  // -----------------------------------------------------------------------
  const handleIncrement = useCallback(
    (mgDef, pathKey, optionId) => {
      const incMgCond = {
        applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
        priceType: mgDef.priceType,
      };
      if (isMultiplierConditionActive(incMgCond)) return;

      const updatedGroups = { ...modifierGroups };
      const mg = updatedGroups[pathKey];
      if (!mg) return;
      const opts = { ...mg.modifierOptions };
      const opt = opts[optionId];
      if (!opt) return;

      const maxPer = mgDef.maxAnySingleModifierItems || 0;
      if (maxPer > 0 && (opt.count || 0) >= maxPer) return;
      const max = mgDef.max || 0;
      const total = Object.values(opts).reduce((s, o) => s + (o.count || 0), 0);
      if (max > 0 && total >= max) return;

      opts[optionId] = { ...opt, count: (opt.count || 0) + 1 };
      updatedGroups[pathKey] = { ...mg, modifierOptions: opts };

      if (opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE) {
        const segs = pathKey.split('__');
        let parentMgPK = null;
        for (let i = segs.length - 1; i >= 0; i--) {
          if (segs[i].startsWith('op_')) { parentMgPK = segs.slice(0, i).join('__'); break; }
        }
        if (parentMgPK) {
          const parentOptId = pathKey.split('__').filter((s) => s.startsWith('op_')).pop()?.replace('op_', '');
          const parentMg = updatedGroups[parentMgPK];
          const parentOpt = parentOptId ? parentMg?.modifierOptions?.[parentOptId] : null;
          if (parentOpt) {
            const multiplierChildren = Object.values(opts)
              .filter((o) => o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE)
              .map((o) => ({ count: o._id === optionId ? (opt.count || 0) + 1 : o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
            const parentBaseAQ = parentOpt.calculatedActualQuantity ?? parentOpt.actualQuantityWithMultiplier ?? parentOpt.originalPrice ?? 0;
            const aqResult = computeActualQtyUpdate({
              parentMgPathKey: parentMgPK,
              parentOptionId: parentOptId,
              parentBaseActualQty: parentBaseAQ,
              multiplierChildren,
              selectedModifierGroups: updatedGroups,
              menuItems: menuMap?.items ?? {},
              menuModifierGroups: menuMap?.modifierGroups ?? {},
            });
            updatedGroups[parentMgPK] = {
              ...parentMg,
              modifierOptions: {
                ...parentMg.modifierOptions,
                [parentOptId]: { ...parentOpt, price: 0, actualQuantityWithMultiplier: 0 },
              },
            };
            applyAncestorDispatch(updatedGroups, aqResult);
          }
        }
      }

      onUpdateModifierGroups(updatedGroups);
    },
    [modifierGroups, onUpdateModifierGroups]
  );

  const handleDecrement = useCallback(
    (mgDef, pathKey, optionId) => {
      const decMgCond = {
        applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
        priceType: mgDef.priceType,
      };
      if (isMultiplierConditionActive(decMgCond)) return;

      const updatedGroups = { ...modifierGroups };
      const mg = updatedGroups[pathKey];
      if (!mg) return;
      const opts = { ...mg.modifierOptions };
      const opt = opts[optionId];
      if (!opt) return;

      if ((opt.count || 0) > 1) {
        opts[optionId] = { ...opt, count: opt.count - 1 };
      } else {
        delete opts[optionId];
        const optPK = buildOptionPathKey(pathKey, optionId);
        Object.keys(updatedGroups).forEach((pk) => {
          if (pk.startsWith(optPK + '__')) delete updatedGroups[pk];
        });
      }

      if (Object.keys(opts).length === 0) {
        delete updatedGroups[pathKey];
      } else {
        updatedGroups[pathKey] = { ...mg, modifierOptions: opts };
      }

      if (opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE) {
        const segs = pathKey.split('__');
        let parentMgPK = null;
        for (let i = segs.length - 1; i >= 0; i--) {
          if (segs[i].startsWith('op_')) { parentMgPK = segs.slice(0, i).join('__'); break; }
        }
        if (parentMgPK && updatedGroups[parentMgPK]) {
          const parentOptId = pathKey.split('__').filter((s) => s.startsWith('op_')).pop()?.replace('op_', '');
          const parentMg = updatedGroups[parentMgPK];
          const parentOpt = parentOptId ? parentMg?.modifierOptions?.[parentOptId] : null;
          if (parentOpt) {
            const currentCount = opt.count || 0;
            const newCount = currentCount > 1 ? currentCount - 1 : 0;
            const multiplierChildren = Object.values(opts)
              .filter((o) => o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE && (o._id !== optionId || newCount > 0))
              .map((o) => ({ count: o._id === optionId ? newCount : o.count ?? 1, originalPrice: o.originalPrice ?? 0 }));
            const parentBaseAQ = parentOpt.calculatedActualQuantity ?? parentOpt.actualQuantityWithMultiplier ?? parentOpt.originalPrice ?? 0;
            const aqResult = computeActualQtyUpdate({
              parentMgPathKey: parentMgPK,
              parentOptionId: parentOptId,
              parentBaseActualQty: parentBaseAQ,
              multiplierChildren,
              selectedModifierGroups: updatedGroups,
              menuItems: menuMap?.items ?? {},
              menuModifierGroups: menuMap?.modifierGroups ?? {},
            });
            updatedGroups[parentMgPK] = {
              ...parentMg,
              modifierOptions: {
                ...parentMg.modifierOptions,
                [parentOptId]: {
                  ...parentOpt,
                  price: multiplierChildren.length > 0 ? 0 : (parentOpt.calculatedPrice ?? parentOpt.originalPrice ?? 0),
                  actualQuantityWithMultiplier: 0,
                },
              },
            };
            applyAncestorDispatch(updatedGroups, aqResult);
          }
        }
      }

      onUpdateModifierGroups(updatedGroups);
    },
    [modifierGroups, onUpdateModifierGroups]
  );

  // Same button size and colors as ModifierGroupsPanel (POS modifier style)
  const BTN_W = 130;
  const BTN_H = 74;
  const GAP = 4;

  // -----------------------------------------------------------------------
  // Collect all modifier groups to display (recursively), inline
  // Returns flat array: [{ mgId, mgDef, pathKey, parentPK, depth }]
  // -----------------------------------------------------------------------
  const allMGs = useMemo(() => {
    const result = [];
    const collectMGs = (mgIds, parentPK, depth) => {
      if (!mgIds) return;
      for (const mgId of mgIds) {
        const mgDef = menuMap?.modifierGroups?.[mgId];
        if (!mgDef) continue;
        const pathKey = buildPathKey(selectedProduct?._id, mgId, parentPK);
        result.push({ mgId, mgDef, pathKey, parentPK, depth });

        // For each selected option in this MG that has nested MGs, recurse
        const mgSelection = modifierGroups[pathKey];
        const selectedOptions = mgSelection?.modifierOptions || {};
        for (const optId of Object.keys(selectedOptions)) {
          const optFullData = menuMap?.items?.[optId];
          if (optFullData?.modifierGroupIds?.length > 0) {
            const optPK = buildOptionPathKey(pathKey, optId);
            collectMGs(optFullData.modifierGroupIds, optPK, depth + 1);
          }
        }
      }
    };
    collectMGs(topMgIds, topParentPK, 0);
    return result;
  }, [topMgIds, topParentPK, menuMap, selectedProduct, modifierGroups]);

  if (!open || !optionItem) return null;

  return (
    <Dialog
      open={open}
      onClose={handleSave}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '85vh',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: POS_COLORS.DISABLED_BG,
          py: 1.25,
          px: 2,
          borderBottom: `1px solid ${POS_COLORS.dividerNew}`,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: 18, color: POS_COLORS.BLACK }}
        >
          {getTranslationValue(optionItem?.name)}
        </Typography>
        <IconButton
          size="small"
          onClick={handleSave}
          sx={{
            color: POS_COLORS.BLACK,
            '&:hover': { bgcolor: POS_COLORS.PRIMARY_RIPPLE },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 2, bgcolor: POS_COLORS.SURFACE }}>
        {allMGs.length === 0 ? (
          <Typography
            sx={{
              py: 3,
              textAlign: 'center',
              color: POS_COLORS.GREY_SECONDARY,
              fontSize: 14,
            }}
          >
            No modifier groups available
          </Typography>
        ) : (
          allMGs.map(({ mgId, mgDef, pathKey, parentPK, depth }) => {
            const mgSelection = modifierGroups[pathKey];
            const selectedOptions = mgSelection?.modifierOptions || {};

            // Resolve parent info for this MG level (for price calculation)
            const { parentOption: localParent, isNested: localIsNested } =
              resolveParentOptionInfo(pathKey, modifierGroups);

            const optionIds = mgDef.optionIds || [];
            const options = optionIds
              .map((oid) => {
                const item = menuMap?.items?.[oid];
                return item ? { ...item, _id: oid } : null;
              })
              .filter(Boolean);

            const mgMultiplierCond = {
              applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
              priceType: mgDef.priceType,
            };
            const mgMultiplierActive =
              isMultiplierConditionActive(mgMultiplierCond) ||
              mgDef.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE ||
              mgDef.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE;
            const aggregatedOpts = mgDef.conditionIsAppliedForProduct
              ? findAllMgInstances(modifierGroups, mgDef._id).flatMap((pk) => {
                  const inst = modifierGroups[pk];
                  return inst ? Object.values(inst.modifierOptions ?? {}) : [];
                })
              : null;
            const totalSelected = getEffectiveOptionsCount(
              aggregatedOpts ?? Object.values(selectedOptions),
              mgDef.applyValidationToItemsActualQuantity,
              mgMultiplierCond
            );
            const minQty = mgDef.min || 0;
            const canMulti = mgDef.canSelectMultipleOptions === true;
            const mgMax = Number(mgDef.max) || 0;
            const minOk =
              (minQty <= 0 || totalSelected >= minQty) &&
              meetsModifierItemMinimums(
                mgDef,
                aggregatedOpts ?? Object.values(selectedOptions)
              );
            const maxOk = mgMax <= 0 || totalSelected <= mgMax;
            const isValid = minOk && maxOk;
            const showValidation =
              minQty > 0 || mgMax > 0 || hasModifierItemMinimums(mgDef);
            const isSingleSelect = mgMax === 1 && !canMulti && !mgMultiplierActive;
            const disableMax =
              !isSingleSelect && !mgMultiplierActive && mgMax > 0 && totalSelected >= mgMax;

            const optionsForCountingNested = aggregatedOpts ?? Object.values(selectedOptions);
            const totalItemCountNested = computeTotalItemCount(
              optionsForCountingNested,
              mgDef.applyValidationToItemsActualQuantity,
              mgMultiplierCond
            );
            const maxAnySingleNested = mgDef.maxAnySingleModifierItems ?? null;
            const maxTotalItemsNested = mgDef.maxTotalNumberOfModifierItems ?? null;
            const isTotalItemsMaxReached =
              maxTotalItemsNested != null && maxTotalItemsNested > 0 && totalItemCountNested >= maxTotalItemsNested;

            return (
              <Box key={pathKey} sx={{ mb: 2, ml: depth > 0 ? depth * 2 : 0 }}>
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'transparent',
                    px: 0.5,
                    minHeight: 32,
                    boxSizing: 'border-box',
                    ...(showValidation && {
                      borderBottom: isValid
                        ? `2px solid ${POS_COLORS.GREEN}`
                        : `2px solid ${POS_COLORS.ERROR}`,
                    }),
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      flex: 1,
                      fontSize: 16,
                      fontWeight: 500,
                      color: POS_COLORS.BLACK,
                    }}
                  >
                    {getTranslationValue(mgDef.name)}
                  </Typography>
                  {showValidation && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.3,
                        ml: 0.5,
                      }}
                    >
                      {isValid ? (
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: 18, color: POS_COLORS.GREEN }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{ fontSize: 18, color: POS_COLORS.ERROR }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: POS_COLORS.BLACK,
                        }}
                      >
                        {minQty}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Options row */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: `${GAP}px`,
                    mt: `${GAP}px`,
                    overflowX: 'auto',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: POS_COLORS.GRAY_SLIDE,
                      borderRadius: 3,
                    },
                  }}
                >
                  {options.map((optItem) => {
                    const optSel = selectedOptions[optItem._id];
                    const optCount = optSel?.count || 0;
                    const isSelected = optCount > 0;
                    const hasNested = optItem.modifierGroupIds?.length > 0;
                    const isSoldOut = isItemSoldOut(optItem);
                    const isFreeOption = isSelected ? (optSel?.freeCount ?? 0) : 0;
                    const menuOpt = menuMap?.items?.[optItem._id] || optItem;
                    const optEffectiveCountNested = optSel
                      ? getEffectiveOptionCount(
                          optSel,
                          mgDef.applyValidationToItemsActualQuantity,
                          mgMultiplierCond
                        )
                      : 0;

                    let isDisabledByMax = (() => {
                      if (isTotalItemsMaxReached) return true;
                      if (
                        isSelected &&
                        maxAnySingleNested != null &&
                        maxAnySingleNested > 0 &&
                        optEffectiveCountNested >= maxAnySingleNested
                      ) return true;
                      if (
                        isSelected &&
                        mgDef.applyValidationToItemsActualQuantity &&
                        mgMax > 0 &&
                        totalSelected >= mgMax
                      ) return true;
                      if (!isSelected) {
                        if (mgMultiplierActive && mgMax > 0 &&
                          (mgDef.applyConditionWithMultiplier || mgMax === 1)) {
                          return false;
                        }
                        return disableMax;
                      }
                      return false;
                    })();
                    if (!isDisabledByMax && !isSelected) {
                      isDisabledByMax = shouldDisableForAncestorMax(
                        optItem._id,
                        pathKey,
                        modifierGroups,
                        menuMap?.modifierGroups || {},
                        menuMap?.items || {}
                      );
                    }
                    const isDisabled = isSoldOut || isDisabledByMax;
                    const menuMg =
                      menuMap?.modifierGroups?.[mgDef._id] || mgDef;
                    const priceCalc = calculatePriceByType({
                      option: {
                        ...menuOpt,
                        priceType: menuOpt?.priceType ?? optItem.priceType,
                      },
                      modifierGroup: {
                        ...menuMg,
                        priceType: menuMg?.priceType ?? mgDef.priceType,
                      },
                      parentOption: localIsNested ? localParent : null,
                      isNestedUnderParentOption: localIsNested,
                    });
                    // Check if option has multiplier priceType
                    const effectiveOptionPriceType = getEffectivePriceType(
                      {
                        ...menuOpt,
                        priceType: menuOpt?.priceType ?? optItem.priceType,
                      },
                      {
                        ...menuMg,
                        priceType: menuMg?.priceType ?? mgDef.priceType,
                      }
                    );
                    const isMultiplierType =
                      effectiveOptionPriceType ===
                        PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE ||
                      effectiveOptionPriceType ===
                        PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE;
                    // multiplierApplyingToParentPrice options should be single-select only (no quantity controls)
                    const isMultiplierToParent =
                      effectiveOptionPriceType ===
                      PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE;
                    const menuPrice = menuOpt?.price ?? 0;

                    let displayPrice = 0;
                    const freshPrice = Number(priceCalc.optionPrice) || 0;
                    if (isSelected && optSel) {
                      if (priceCalc.shouldZeroParentPrice) {
                        displayPrice = freshPrice > 0
                          ? freshPrice
                          : Number(optSel.calculatedPrice) || 0;
                      } else {
                        const currentPrice = Number(optSel.price);
                        const storedCalc = Number(optSel.calculatedPrice);
                        if (currentPrice === 0 && storedCalc > 0 && !isMultiplierType) {
                          displayPrice = 0;
                        } else if (storedCalc > 0) {
                          displayPrice = storedCalc;
                        } else if (freshPrice > 0) {
                          displayPrice = freshPrice;
                        } else if (!isMultiplierType && menuPrice > 0) {
                          displayPrice = menuPrice;
                        }
                      }
                    } else {
                      if (freshPrice > 0) {
                        displayPrice = freshPrice;
                      } else if (!isMultiplierType && menuPrice > 0) {
                        displayPrice = menuPrice;
                      }
                    }

                    return (
                      <Box
                        key={optItem._id}
                        onClick={() => {
                          if (!isDisabled)
                            handleOptionClick(mgDef, pathKey, optItem);
                        }}
                        sx={{
                          flexShrink: 0,
                          position: 'relative',
                          borderRadius: '12px',
                          width: BTN_W,
                          height: BTN_H,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.5,
                          boxSizing: 'border-box',
                          border: isSelected
                            ? `2px solid ${POS_COLORS.GREEN}`
                            : '1px solid transparent',
                          bgcolor: isDisabled
                            ? POS_COLORS.DISABLED_BG
                            : isSelected
                            ? POS_COLORS.MODIFIER_BG_SELECTED
                            : POS_COLORS.MODIFIER_BG,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.7 : 1,
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                          '&:hover': {
                            bgcolor: isDisabled
                              ? undefined
                              : POS_COLORS.MODIFIER_BG_HOVER,
                          },
                        }}
                      >
                        {/* Selected check – top-right (always show for nested options) */}
                        {isSelected && (!canMulti || hasNested) && (
                          <CheckCircleIcon
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              fontSize: 20,
                              color: '#00984F',
                            }}
                          />
                        )}
                        {isSoldOut && (
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: '#E21508',
                            }}
                          >
                            Unavailable
                          </Typography>
                        )}
                        {isFreeOption > 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 8,
                              zIndex: 2,
                              backgroundColor: POS_COLORS.GREEN,
                              borderRadius: '4px',
                              px: '5px',
                              py: '2px',
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {isFreeOption === 1 ? 'FREE' : `${isFreeOption} FREE`}
                            </Typography>
                          </Box>
                        )}
                        {/* Option name + nested arrow */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            flex: 1,
                            minHeight: 0,
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontWeight: 500,
                              color: POS_COLORS.BLACK,
                              fontSize: 16,
                              lineHeight: 1.2,
                              wordBreak: 'break-word',
                              textAlign: 'center',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {getTranslationValue(optItem.name)}
                          </Typography>
                          {hasNested && !isSelected && (
                            <ChevronRightIcon
                              sx={{
                                position: 'absolute',
                                right: 4,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: 20,
                                color: POS_COLORS.BLACK,
                              }}
                            />
                          )}
                        </Box>
                        {/* Price display: multiplier types show "Nx" before selection, "$X.XX" after */}
                        {isMultiplierType && !isSelected && menuPrice > 0 ? (
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: POS_COLORS.BLACK_SECONDARY,
                              mt: 0.2,
                            }}
                          >
                            {menuPrice}x
                          </Typography>
                        ) : displayPrice !== 0 ? (
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: POS_COLORS.BLACK_SECONDARY,
                              mt: 0.2,
                            }}
                          >
                            {displayPrice > 0 ? '+' : ''}$
                            {Math.abs(displayPrice).toFixed(2)}
                          </Typography>
                        ) : null}
                        {/* Multiplier count for selected nested options (matches POS "1x" badge) */}
                        {isSelected && hasNested && (
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: POS_COLORS.BLACK_SECONDARY,
                              mt: 0.2,
                            }}
                          >
                            {optCount}x
                          </Typography>
                        )}
                        {isSelected && canMulti && !hasNested && !isMultiplierToParent && !mgMultiplierActive && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.3,
                              mt: 0.3,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                p: 0.2,
                                border: `1px solid ${POS_COLORS.BLACK}`,
                                borderRadius: '50%',
                              }}
                              onClick={() =>
                                handleDecrement(mgDef, pathKey, optItem._id)
                              }
                            >
                              <RemoveIcon
                                sx={{ fontSize: 14, color: POS_COLORS.BLACK }}
                              />
                            </IconButton>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: 13,
                                minWidth: 16,
                                textAlign: 'center',
                                color: POS_COLORS.BLACK,
                              }}
                            >
                              {optCount}
                            </Typography>
                            <IconButton
                              size="small"
                              disabled={isDisabled || isDisabledByMax}
                              sx={{
                                p: 0.2,
                                border: `1px solid ${POS_COLORS.BLACK}`,
                                borderRadius: '50%',
                                '&.Mui-disabled': { opacity: 0.4 },
                              }}
                              onClick={() =>
                                handleIncrement(mgDef, pathKey, optItem._id)
                              }
                            >
                              <AddIcon
                                sx={{ fontSize: 14, color: POS_COLORS.BLACK }}
                              />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${POS_COLORS.DIVIDER}`,
          bgcolor: POS_COLORS.WHITE,
          gap: 1,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            textTransform: 'uppercase',
            color: POS_COLORS.BLACK_SECONDARY,
            fontWeight: 600,
            fontSize: 15,
            '&:hover': { bgcolor: POS_COLORS.PRIMARY_RIPPLE },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            textTransform: 'uppercase',
            bgcolor: POS_COLORS.PRIMARY,
            color: POS_COLORS.WHITE,
            '&:hover': { bgcolor: POS_COLORS.PRIMARY_HOVER },
            fontWeight: 600,
            fontSize: 15,
            borderRadius: '8px',
            px: 2,
            py: 1.25,
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NestedLayoutModal;
